import { createReducer } from '@reduxjs/toolkit';
import type { RootState } from 'reducers/root';

import {
  closeWidget,
  navigateBack,
  navigateTo,
  openWidget,
  resetWidget,
  setIsMobile,
  setIsWidgetVisible,
  setSelectedAnswerIndex,
  setVisualViewportOffset,
  startOver,
  setImageViewer,
  clearImageViewer,
  setNavigateBackAction,
  setWindowDimensions,
  setSelectedTab,
} from 'actions/main';
import { KNOWLEDGE_SEARCH_PAGE, WORKFLOWS_PAGE } from 'constants/pageConsts';
import initialState from './initialState';
import { selectIsMobileSDK } from 'reducers/workflows';
import { selectLaunchStyle } from 'reducers/config';
import type { WidgetStyle } from 'api/types';

export interface ImageViewerSettings {
  alt: string;
  src: string;
}

export type NavigateBackAction = 'close' | undefined;

export type IframeRect = {
  bottom: number;
  height: number;
  left: number;
  right: number;
  top: number;
  width: number;
};

export type WindowDimensions = {
  iframeRect?: IframeRect;
  innerHeight: number;
  innerWidth: number;
  outerHeight: number;
  outerWidth: number;
};

export interface MainPageState {
  history: Array<string>;
  imageViewer: ImageViewerSettings;
  isHelpButtonVisible?: boolean;
  isMobile?: boolean;
  isWidgetVisible: boolean;
  navigateBackAction: NavigateBackAction;
  selectedAnswerIndex: number;
  selectedTab: number;
  visualViewportOffset: {
    offsetLeft: number;
    offsetTop: number;
  };
  windowDimensions: WindowDimensions;
}

const mainReducer = createReducer(initialState, builder => {
  builder.addCase(navigateTo, (state, action) => {
    const history = state.history;
    return { ...state, history: [...history, action.payload] };
  });
  builder.addCase(navigateBack, state => {
    const history = [...state.history];
    if (history.length > 1) history.pop();

    if (state.navigateBackAction === 'close') {
      // same logic but also close the widget
      return {
        ...state,
        history,
        imageViewer: initialState.imageViewer,
        isHelpButtonVisible: true,
        isWidgetVisible: false,
        navigateBackAction: undefined,
      };
    } else {
      return { ...state, history, navigateBackAction: undefined };
    }
  });

  builder.addCase(resetWidget, state => ({
    ...initialState,
    history: state.history,
  }));

  builder.addCase(startOver, state => ({
    ...initialState,
    history: state.history,
    isHelpButtonVisible: state.isHelpButtonVisible,
    isMobile: state.isMobile,
    isWidgetVisible: state.isWidgetVisible,
  }));
  builder.addCase(setIsMobile, (state, action) => ({
    ...state,
    isMobile: action.payload,
  }));

  builder.addCase(setIsWidgetVisible, (state, action) => {
    return {
      ...state,
      isWidgetVisible: action.payload,
    };
  });
  builder.addCase(openWidget, state => {
    return {
      ...state,
      imageViewer: initialState.imageViewer,
      isHelpButtonVisible: false,
      isWidgetVisible: true,
    };
  });
  builder.addCase(closeWidget, state => {
    return {
      ...state,
      imageViewer: initialState.imageViewer,
      isHelpButtonVisible: true,
      isWidgetVisible: false,
    };
  });
  builder.addCase(setSelectedAnswerIndex, (state, action) => ({
    ...state,
    selectedAnswerIndex: action.payload,
  }));
  builder.addCase(setVisualViewportOffset, (state, action) => ({
    ...state,
    visualViewportOffset: action.payload,
  }));
  builder.addCase(setImageViewer, (state, action) => ({
    ...state,
    imageViewer: action.payload,
  }));
  builder.addCase(clearImageViewer, state => ({
    ...state,
    imageViewer: initialState.imageViewer,
  }));
  builder.addCase(setNavigateBackAction, (state, action) => ({
    ...state,
    navigateBackAction: action.payload,
  }));
  builder.addCase(setWindowDimensions, (state, action) => ({
    ...state,
    windowDimensions: action.payload,
  }));
  builder.addCase(setSelectedTab, (state, action) => ({
    ...state,
    selectedTab: action.payload,
  }));
});

export default mainReducer;

export const selectHistory = (state: RootState): MainPageState['history'] =>
  state.main.history ?? [WORKFLOWS_PAGE];

export const selectCurrentPage = (state: RootState): string => {
  // If history is undefined default to initial state
  const history = state.main.history ?? [WORKFLOWS_PAGE];
  return history[history.length - 1] ?? '';
};

export const selectIsMobile = (state: RootState): boolean =>
  Boolean(state.main.isMobile || selectIsMobileSDK(state));

export const selectWidgetStyle = (state: RootState): WidgetStyle => {
  if (selectIsMobile(state)) {
    return 'mobile';
  } else if (selectLaunchStyle(state) === 'floating_bar') {
    return 'floating_bar';
  } else {
    return 'standard';
  }
};

export const selectVisualViewportOffset = (
  state: RootState,
): MainPageState['visualViewportOffset'] => state.main.visualViewportOffset;

export const selectIsWidgetVisible = (
  state: RootState,
): MainPageState['isWidgetVisible'] => state.main.isWidgetVisible;
export const selectIsHelpButtonVisible = (
  state: RootState,
): MainPageState['isHelpButtonVisible'] => state.main.isHelpButtonVisible;
export const selectSelectedAnswerIndex = (
  state: RootState,
): MainPageState['selectedAnswerIndex'] => state.main.selectedAnswerIndex;

export const selectIsArticlePage = (state: RootState): boolean => {
  const history = state.main.history;
  return history[history.length - 1] === 'ARTICLE_PAGE';
};
export const selectImageViewer = (
  state: RootState,
): MainPageState['imageViewer'] => state.main.imageViewer;
export const selectIsViewingImage = (state: RootState): boolean =>
  !!state.main.imageViewer.src;
export const selectWindowDimensions = (
  state: RootState,
): MainPageState['windowDimensions'] => state.main.windowDimensions;
export const selectIsKnowledgeSearchPage = (state: RootState): boolean => {
  const history = state.main.history;
  return history[history.length - 1] === KNOWLEDGE_SEARCH_PAGE;
};

export const selectSelectedTab = (
  state: RootState,
): MainPageState['selectedTab'] => {
  return state.main.selectedTab;
};
